import { FeaturePermission } from "@/__generated__/featurePermissions";
import { UserMedspa } from "@/auth/useAuthState";
import routes from "@/routes";
import { routeToRegex } from "@/utils/paths";

function normalizeUrlPattern(urlPattern: string): string {
  return urlPattern.replace(/\[.*?]/g, ":dynamicParam").replace(/\/$/, ""); // Remove trailing slash
}

function findNextFileRoute(pathname: string) {
  for (const route of routes) {
    const regex = routeToRegex(route);
    if (regex.test(pathname)) {
      return route;
    }
  }
  return null;
}

// exact match pattern with pattern
// ex. /[medspaId]/clients/[slug] will match /[medspaId]/clients/[slug] but will not match /[medspaId]/clients/charts
function hasPermissionToNextRoute(
  route: string,
  urlPatternsPermissions: string[]
) {
  const transformedRoute = normalizeUrlPattern(route);

  return urlPatternsPermissions.some((pattern) => {
    return transformedRoute === normalizeUrlPattern(pattern);
  });
}

function isExternalUrl(url: string): boolean {
  try {
    return new URL(url).origin !== location.origin;
  } catch {
    // If URL constructor throws, it's relative/internal URL
    return false;
  }
}

export function hasPermissionsToAccessUrl(
  nextUrl: string,
  urlPatternsPermissions: string[]
): boolean {
  if (!nextUrl) return false;
  const strippedUrl = nextUrl.split("?")[0];
  const nextRoutePattern = findNextFileRoute(strippedUrl);

  if (nextRoutePattern === null) {
    // Distinguish internal vs external URLs
    return isExternalUrl(strippedUrl);
  }

  return hasPermissionToNextRoute(nextRoutePattern, urlPatternsPermissions);
}

export function hasUserMedspaFeaturePermission(
  userMedspa: Pick<UserMedspa, "role" | "featurePermissions"> | undefined,
  permission: FeaturePermission,
  oldRoles: string[] = []
) {
  return userMedspa?.featurePermissions?.length > 0
    ? userMedspa?.featurePermissions?.some(
        (perm) => perm.codename === permission
      )
    : oldRoles.includes(userMedspa?.role || "");
}
